import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * ET4: utskrift i apparna. window.print() gör ingenting i WKWebView (iOS) eller Android WebView, och
 * webbvyns egen utskrift gick inte att lita på för etikettarken (fel skala/arken bredvid varandra på
 * telefonen). Därför skriver apparna ut en färdig PDF med fasta mått (labelCanvas.ts) via
 * NativePrint-pluginet: iOS PrintPlugin i ios/App/App/ViewController.swift, Android NativePrintPlugin.java.
 * printHtml() skriver ut ett fristående HTML-dokument (avtal).
 */

export interface PrintPdfOptions {
  jobName?: string;
  /** Sidstorlek i mm – A4 för ark, etikettens storlek för etikettskrivare (väljer papper). */
  pageWidthMm: number;
  pageHeightMm: number;
}

/** Vad appen säger efter utskriften; pappret är det skrivaren valde (iOS), om det finns. */
export interface PrintPdfResult {
  completed: boolean;
  paperWidthMm?: number;
  paperHeightMm?: number;
}

const NativePrint = registerPlugin<{
  printPdf(options: PrintPdfOptions & { data: string }): Promise<PrintPdfResult>;
  printHtml(options: { html: string; jobName?: string }): Promise<{ completed: boolean }>;
  showPdf(options: { data: string; fileName: string }): Promise<void>;
}>('NativePrint');

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function nativePlatform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

/** Finns NativePrint-pluginet i appen (för loggen)? */
export function nativePrintAvailable(): boolean {
  try {
    return Capacitor.isPluginAvailable('NativePrint');
  } catch {
    return false;
  }
}

/** Appbyggen från före ET4 saknar pluginet (eller printPdf) – där går det inte att skriva ut alls. */
function nativePrintOrThrow(): typeof NativePrint {
  if (!Capacitor.isPluginAvailable('NativePrint')) {
    throw new Error('Utskrift kräver en nyare version av appen. Uppdatera appen, eller skriv ut från qvenari.com i en webbläsare.');
  }
  return NativePrint;
}

function nativeError(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e ?? '');
  if (/not implemented|unimplemented/i.test(msg)) {
    return new Error('Utskrift kräver en nyare version av appen. Uppdatera appen, eller skriv ut från qvenari.com i en webbläsare.');
  }
  return new Error(msg ? `Utskriften gick inte att starta: ${msg}` : 'Utskriften gick inte att starta.');
}

export function toBase64(bytes: Uint8Array): string {
  let bin = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) bin += String.fromCharCode(...bytes.subarray(i, i + step));
  return btoa(bin);
}

/**
 * Skriver ut en färdig PDF i appen (systemets utskriftsdialog, 100 %). Löses när dialogen stängts och
 * skickar då 'afterprint' (som webbläsaren gör efter window.print()). Kastar med ett begripligt fel.
 */
export async function printPdf(pdf: Uint8Array, options: PrintPdfOptions): Promise<PrintPdfResult> {
  const plugin = nativePrintOrThrow();
  try {
    return await plugin.printPdf({ ...options, data: toBase64(pdf) });
  } catch (e) {
    throw nativeError(e);
  } finally {
    window.dispatchEvent(new Event('afterprint'));
  }
}

/**
 * Visar en PDF som den är (ET8). Apparna: systemets PDF-visning (iOS Quick Look, Android en PDF-app) med
 * dela/spara. Webbläsaren: i fliken `tab` (öppnad direkt i klicket), annars laddas filen ner.
 */
export async function showPdf(pdf: Uint8Array, options: { fileName: string; tab?: Window | null }): Promise<void> {
  if (!isNativeApp()) {
    const url = URL.createObjectURL(new Blob([pdf as BlobPart], { type: 'application/pdf' }));
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
    if (options.tab && !options.tab.closed) {
      options.tab.location.href = url;
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = options.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  const plugin = nativePrintOrThrow();
  try {
    await plugin.showPdf({ data: toBase64(pdf), fileName: options.fileName });
  } catch (e) {
    const err = nativeError(e);
    throw new Error(err.message.replace('Utskriften gick inte att starta', 'PDF:en gick inte att visa'));
  }
}

/** Skriver ut ett fristående HTML-dokument (t.ex. ett avtal). I webbläsaren i ett nytt fönster. */
export async function printHtml(html: string, jobName?: string): Promise<void> {
  if (!isNativeApp()) {
    const w = window.open('', '_blank');
    if (!w) throw new Error('Utskriftsfönstret blockerades. Tillåt popup-fönster och försök igen.');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 400);
    return;
  }
  const plugin = nativePrintOrThrow();
  try {
    await plugin.printHtml({ html, jobName });
  } catch (e) {
    throw nativeError(e);
  }
}
